import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen, MessageSquare, Volume2, Wrench, CheckCircle, XCircle, ThumbsUp,
  History, Save, Loader2, Plus, Trash2, AlertTriangle, ShieldCheck, ShieldAlert,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import AdminStatusStrip from '../components/AdminStatusStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/training`;

const TABS = [
  { id: 'doctrine', label: 'Founder Doctrine', Icon: BookOpen },
  { id: 'prompts',  label: 'Agent Prompts',    Icon: MessageSquare },
  { id: 'tone',     label: 'Tone Rules',       Icon: Volume2 },
  { id: 'playbooks',label: 'Playbooks',        Icon: Wrench },
  { id: 'approved', label: 'Approved',         Icon: CheckCircle },
  { id: 'rejected', label: 'Rejected',         Icon: XCircle },
  { id: 'feedback', label: 'Feedback',         Icon: ThumbsUp },
  { id: 'log',      label: 'Prompt Log',       Icon: History },
];

const AGENTS = ['intake', 'summary', 'missing_info', 'reply', 'follow_up', 'safety', 'marketing'];
const FEEDBACK_KINDS = [
  ['approve', 'Approve'],
  ['excellent_output', 'Excellent output'],
  ['needs_rewrite', 'Needs rewrite'],
  ['too_robotic', 'Too robotic'],
  ['missing_info', 'Missing info'],
  ['unsafe_claim', 'Unsafe claim'],
  ['wrong_tone', 'Wrong tone'],
];

const authHeaders = () => ({}); // app uses HTTP-only cookies via withCredentials

const useApi = () => {
  return useMemo(() => ({
    get: (path) => axios.get(`${API}${path}`, { headers: authHeaders(), withCredentials: true }),
    put: (path, body) => axios.put(`${API}${path}`, body, { headers: authHeaders(), withCredentials: true }),
    post: (path, body) => axios.post(`${API}${path}`, body, { headers: authHeaders(), withCredentials: true }),
    del: (path) => axios.delete(`${API}${path}`, { headers: authHeaders(), withCredentials: true }),
  }), []);
};

const TrainingCenter = () => {
  const [tab, setTab] = useState('doctrine');
  useEffect(() => { document.title = 'HussarAi · Training Center'; }, []);

  return (
    <div className="hussar-tc-page" data-testid="training-center-page">
      <header className="hussar-tc-header">
        <div className="hussar-tc-header-inner">
          <div>
            <Link to="/" className="hussar-tc-back" data-testid="tc-back">← HussarAi</Link>
            <h1 className="hussar-tc-h1" data-testid="tc-h1">Training Center</h1>
            <p className="hussar-tc-sub">Founder-led prompt improvement. Admin only.</p>
            <p className="hussar-tc-sub">
              <Link to="/admin/company-brain" className="hussar-tc-back" data-testid="tc-link-company-brain">
                → Company Brain (services / pricing / tone)
              </Link>
            </p>
            <SecurityPostureBadge />
          </div>
          <div className="hussar-tc-badge" data-testid="tc-admin-badge">
            <AlertTriangle className="w-4 h-4" /> Admin only · Not exposed to public users
          </div>
        </div>
        <nav className="hussar-tc-tabs" data-testid="tc-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`hussar-tc-tab ${tab === id ? 'is-active' : ''}`}
              data-testid={`tc-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </header>

      <main className="hussar-tc-main">
        <AdminStatusStrip />
        {tab === 'doctrine'  && <DoctrineTab />}
        {tab === 'prompts'   && <PromptsTab />}
        {tab === 'tone'      && <ToneTab />}
        {tab === 'playbooks' && <PlaybooksTab />}
        {tab === 'approved'  && <ExamplesTab type="approved" />}
        {tab === 'rejected'  && <ExamplesTab type="rejected" />}
        {tab === 'feedback'  && <FeedbackTab setTab={setTab} />}
        {tab === 'log'       && <PromptLogTab />}
      </main>
    </div>
  );
};

/* ---------- Security Posture Badge ---------- */
const SecurityPostureBadge = () => {
  const [posture, setPosture] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/admin/security/posture`, { withCredentials: true })
      .then((r) => { if (!cancelled) setPosture(r.data); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, []);

  if (err) return null;
  if (!posture) {
    return (
      <div className="hussar-tc-posture hussar-tc-posture-loading" data-testid="tc-posture-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Security posture…
      </div>
    );
  }

  const healthy = posture.status === 'healthy';
  const Icon = healthy ? ShieldCheck : ShieldAlert;
  return (
    <div
      className={`hussar-tc-posture ${healthy ? 'hussar-tc-posture-ok' : 'hussar-tc-posture-warn'}`}
      data-testid={`tc-posture-${posture.status}`}
      title={posture.summary}
    >
      <Icon className="w-3 h-3" />
      <span data-testid="tc-posture-summary">
        {healthy
          ? 'Security posture: Cookies HttpOnly + Secure ✓ · LocalStorage clean ✓'
          : `Security posture: ${posture.summary}`}
      </span>
    </div>
  );
};

/* ---------- 1. Doctrine ---------- */
const DoctrineTab = () => {
  const api = useApi();
  const [text, setText] = useState('');
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get('/doctrine').then(r => {
      setText(r.data.text || '');
      setMeta({ updatedAt: r.data.updatedAt, updatedBy: r.data.updatedBy });
    }).finally(() => setLoading(false));
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/doctrine', { text });
      setSavedAt(new Date().toISOString());
    } finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="hussar-tc-spinner animate-spin" />;
  return (
    <Card className="hussar-tc-card" data-testid="tc-doctrine">
      <h2 className="hussar-tc-h2">Founder Doctrine</h2>
      <p className="hussar-tc-help">Core HussarAi positioning. This is the source of truth every agent should defer to.</p>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        maxLength={4000}
        data-testid="tc-doctrine-text"
        className="hussar-tc-textarea"
      />
      <div className="hussar-tc-row">
        <Button onClick={save} disabled={saving} data-testid="tc-doctrine-save" className="hussar-btn-primary">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save doctrine
        </Button>
        <div className="hussar-tc-meta">
          {savedAt ? `Saved · ${new Date(savedAt).toLocaleString()}` : meta.updatedAt ? `Updated ${new Date(meta.updatedAt).toLocaleString()} by ${meta.updatedBy || '—'}` : '—'}
        </div>
      </div>
    </Card>
  );
};

/* ---------- 2. Agent Prompts ---------- */
const PromptsTab = () => {
  const api = useApi();
  const [data, setData] = useState({});
  const [active, setActive] = useState('intake');
  const [draft, setDraft] = useState({ body: '', note: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get('/prompts');
    setData(r.data.prompts || {});
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const cur = data[active];
    if (cur) setDraft({ body: cur.body || '', note: '' });
  }, [data, active]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/prompts/${active}`, draft);
      await load();
      setDraft(d => ({ ...d, note: '' }));
    } finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="hussar-tc-spinner animate-spin" />;
  const current = data[active] || {};

  return (
    <div data-testid="tc-prompts">
      <div className="hussar-tc-agent-row" data-testid="tc-agent-row">
        {AGENTS.map(a => (
          <button
            key={a}
            type="button"
            onClick={() => setActive(a)}
            className={`hussar-tc-agent-pill ${active === a ? 'is-active' : ''}`}
            data-testid={`tc-agent-${a}`}
          >
            {a.replace('_', ' ')}
            <span className="hussar-tc-agent-version">v{data[a]?.version ?? 0}</span>
          </button>
        ))}
      </div>

      <Card className="hussar-tc-card">
        <h2 className="hussar-tc-h2">{active.replace('_', ' ')} agent · v{current.version || 0}</h2>
        <p className="hussar-tc-help">Editing publishes a new version. Previous body is archived in history (visible below).</p>
        <Textarea
          value={draft.body}
          onChange={(e) => setDraft(d => ({ ...d, body: e.target.value }))}
          rows={12}
          maxLength={8000}
          data-testid="tc-prompt-body"
          className="hussar-tc-textarea"
        />
        <Input
          value={draft.note}
          onChange={(e) => setDraft(d => ({ ...d, note: e.target.value }))}
          placeholder="Reason for change (e.g. 'tightened tone — no fake urgency')"
          maxLength={400}
          data-testid="tc-prompt-note"
        />
        <div className="hussar-tc-row">
          <Button onClick={save} disabled={saving || !draft.body.trim()} data-testid="tc-prompt-save" className="hussar-btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Publish new version
          </Button>
          <Button variant="outline" onClick={() => setShowHistory(s => !s)} data-testid="tc-prompt-toggle-history">
            <History className="w-4 h-4" />
            {showHistory ? 'Hide' : 'Show'} history ({current.history?.length || 0})
          </Button>
          <div className="hussar-tc-meta">
            {current.updatedAt ? `Last updated ${new Date(current.updatedAt).toLocaleString()}` : '—'}
          </div>
        </div>
        {showHistory && (
          <ul className="hussar-tc-history-list" data-testid="tc-prompt-history-list">
            {(current.history || []).map((h, i) => (
              <li key={h.version ?? `idx-${i}`} data-testid={`tc-prompt-history-${i}`}>
                <div className="hussar-tc-history-head">
                  <span>v{h.version}</span>
                  <span>{h.updatedAt ? new Date(h.updatedAt).toLocaleString() : '—'}</span>
                  <span>{h.updatedBy || '—'}</span>
                </div>
                {h.note && <div className="hussar-tc-history-note">{h.note}</div>}
              </li>
            ))}
            {(current.history || []).length === 0 && <li className="hussar-tc-empty">No previous versions yet.</li>}
          </ul>
        )}
      </Card>
    </div>
  );
};

/* ---------- 3. Tone Rules ---------- */
const ToneTab = () => {
  const api = useApi();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/tone-rules').then(r => setRules(r.data.rules || [])).finally(() => setLoading(false));
  }, [api]);

  const update = (i, v) => setRules(rs => rs.map((x, ix) => ix === i ? v : x));
  const remove = (i) => setRules(rs => rs.filter((_, ix) => ix !== i));
  const add = () => setRules(rs => [...rs, '']);
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tone-rules', { rules: rules.filter(r => r.trim()) });
    } finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="hussar-tc-spinner animate-spin" />;
  return (
    <Card className="hussar-tc-card" data-testid="tc-tone">
      <h2 className="hussar-tc-h2">Tone Rules</h2>
      <p className="hussar-tc-help">One rule per line. Used by the Reply Agent and Safety Agent.</p>
      <ul className="hussar-tc-rules-list" data-testid="tc-tone-list">
        {rules.map((r, i) => (
          // DEFERRED (P2): free-form mutable strings without stable IDs —
          // switching to value-as-key is unsafe (rules can be duplicate
          // empty strings while editing) and adding stable IDs requires a
          // backend ToneRules API-shape change. Tracked in backlog.
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className="hussar-tc-rule-row">
            <Input
              value={r}
              onChange={(e) => update(i, e.target.value)}
              data-testid={`tc-tone-rule-${i}`}
              maxLength={120}
            />
            <Button variant="ghost" onClick={() => remove(i)} data-testid={`tc-tone-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </li>
        ))}
      </ul>
      <div className="hussar-tc-row">
        <Button variant="outline" onClick={add} data-testid="tc-tone-add"><Plus className="w-4 h-4" />Add rule</Button>
        <Button onClick={save} disabled={saving} className="hussar-btn-primary" data-testid="tc-tone-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save rules
        </Button>
      </div>
    </Card>
  );
};

/* ---------- 4. Industry Playbooks ---------- */
const PlaybooksTab = () => {
  const api = useApi();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('hvac');
  const [draft, setDraft] = useState({ industry: 'hvac', label: '', checklist: [''], notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get('/playbooks');
    setList(r.data.playbooks || []);
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const cur = list.find(p => p.industry === active);
    if (cur) setDraft({ industry: cur.industry, label: cur.label, checklist: cur.checklist.length ? cur.checklist : [''], notes: cur.notes || '' });
    else setDraft({ industry: active, label: '', checklist: [''], notes: '' });
  }, [list, active]);

  const updateItem = (i, v) => setDraft(d => ({ ...d, checklist: d.checklist.map((x, ix) => ix === i ? v : x) }));
  const removeItem = (i) => setDraft(d => ({ ...d, checklist: d.checklist.filter((_, ix) => ix !== i) }));
  const addItem = () => setDraft(d => ({ ...d, checklist: [...d.checklist, ''] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/playbooks/${draft.industry}`, {
        industry: draft.industry,
        label: draft.label || draft.industry,
        checklist: draft.checklist.filter(c => c.trim()),
        notes: draft.notes,
      });
      await load();
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete playbook "${draft.industry}"?`)) return;
    await api.del(`/playbooks/${draft.industry}`);
    setActive('hvac');
    await load();
  };

  if (loading) return <Loader2 className="hussar-tc-spinner animate-spin" />;
  return (
    <div data-testid="tc-playbooks">
      <div className="hussar-tc-agent-row">
        {list.map(p => (
          <button key={p.industry} type="button" onClick={() => setActive(p.industry)}
            className={`hussar-tc-agent-pill ${active === p.industry ? 'is-active' : ''}`}
            data-testid={`tc-playbook-${p.industry}`}>
            {p.label || p.industry}
          </button>
        ))}
        <button type="button" onClick={() => setActive('new')}
          className={`hussar-tc-agent-pill ${active === 'new' ? 'is-active' : ''}`}
          data-testid="tc-playbook-new">
          <Plus className="w-3 h-3" />New industry
        </button>
      </div>

      <Card className="hussar-tc-card">
        <h2 className="hussar-tc-h2">{draft.label || draft.industry || 'New playbook'}</h2>
        <p className="hussar-tc-help">Checklist drives the Missing-Info Agent for this industry.</p>
        <div className="hussar-tc-row">
          <Input value={draft.industry} onChange={(e) => setDraft(d => ({ ...d, industry: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} placeholder="industry slug (e.g. hvac)" data-testid="tc-playbook-industry" />
          <Input value={draft.label} onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))} placeholder="display label" data-testid="tc-playbook-label" />
        </div>
        <ul className="hussar-tc-rules-list">
          {draft.checklist.map((c, i) => (
            // DEFERRED (P2): same as tone rules above — free-form mutable
            // strings, no stable IDs in the Playbook API yet. Tracked in
            // backlog.
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className="hussar-tc-rule-row">
              <Input value={c} onChange={(e) => updateItem(i, e.target.value)} data-testid={`tc-playbook-item-${i}`} />
              <Button variant="ghost" onClick={() => removeItem(i)} data-testid={`tc-playbook-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
        </ul>
        <Textarea value={draft.notes} onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))} placeholder="Notes (optional)" rows={3} maxLength={2000} data-testid="tc-playbook-notes" />
        <div className="hussar-tc-row">
          <Button variant="outline" onClick={addItem} data-testid="tc-playbook-add"><Plus className="w-4 h-4" />Add item</Button>
          <Button onClick={save} disabled={saving || !draft.industry.trim() || !draft.checklist.some(c => c.trim())} className="hussar-btn-primary" data-testid="tc-playbook-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save playbook
          </Button>
          {active !== 'new' && active !== 'hvac' && (
            <Button variant="ghost" onClick={remove} data-testid="tc-playbook-delete"><Trash2 className="w-4 h-4" />Delete</Button>
          )}
        </div>
      </Card>
    </div>
  );
};

/* ---------- 5/6. Examples ---------- */
const ExamplesTab = ({ type }) => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ type, rawInquiry: '', summary: '', missingInfo: '', reply: '', followUp: '', rejectionReason: '', notes: '' });

  const load = useCallback(async () => {
    const r = await api.get(`/examples?type=${type}`);
    setItems(r.data.examples || []);
    setLoading(false);
  }, [api, type]);
  useEffect(() => { setDraft(d => ({ ...d, type })); load(); }, [type, load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/examples', draft);
      setDraft({ type, rawInquiry: '', summary: '', missingInfo: '', reply: '', followUp: '', rejectionReason: '', notes: '' });
      await load();
    } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm('Delete example?')) return; await api.del(`/examples/${id}`); await load(); };

  return (
    <div data-testid={`tc-examples-${type}`}>
      <Card className="hussar-tc-card">
        <h2 className="hussar-tc-h2">{type === 'approved' ? 'Approved example' : 'Rejected example'}</h2>
        <Textarea placeholder="Raw customer inquiry" value={draft.rawInquiry} onChange={(e) => setDraft(d => ({ ...d, rawInquiry: e.target.value }))} rows={4} data-testid="tc-example-raw" />
        <Textarea placeholder={type === 'approved' ? 'Ideal summary' : 'Generated summary (bad)'} value={draft.summary} onChange={(e) => setDraft(d => ({ ...d, summary: e.target.value }))} rows={2} data-testid="tc-example-summary" />
        <Textarea placeholder={type === 'approved' ? 'Ideal missing info' : 'Generated missing info (bad)'} value={draft.missingInfo} onChange={(e) => setDraft(d => ({ ...d, missingInfo: e.target.value }))} rows={3} data-testid="tc-example-missing" />
        <Textarea placeholder={type === 'approved' ? 'Ideal reply' : 'Generated reply (bad)'} value={draft.reply} onChange={(e) => setDraft(d => ({ ...d, reply: e.target.value }))} rows={4} data-testid="tc-example-reply" />
        <Textarea placeholder={type === 'approved' ? 'Ideal follow-up' : 'Generated follow-up (bad)'} value={draft.followUp} onChange={(e) => setDraft(d => ({ ...d, followUp: e.target.value }))} rows={2} data-testid="tc-example-followup" />
        {type === 'rejected' && (
          <Input placeholder="Rejection reason (too_robotic / missing_info / unsafe_claim / poor_polish / too_long / too_salesy / unclear_followup)"
            value={draft.rejectionReason} onChange={(e) => setDraft(d => ({ ...d, rejectionReason: e.target.value }))} data-testid="tc-example-reason" />
        )}
        <Input placeholder="Notes (optional)" value={draft.notes} onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))} data-testid="tc-example-notes" />
        <Button onClick={save} disabled={saving || !draft.rawInquiry.trim()} className="hussar-btn-primary" data-testid="tc-example-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Save example
        </Button>
      </Card>

      {loading ? <Loader2 className="hussar-tc-spinner animate-spin" /> : (
        <ul className="hussar-tc-examples-list" data-testid="tc-examples-list">
          {items.map(ex => (
            <li key={ex.id} className="hussar-tc-example-row">
              <div className="hussar-tc-example-meta">
                {ex.rejectionReason && <span className="hussar-tc-tag-reject">{ex.rejectionReason}</span>}
                <span className="hussar-tc-meta">{ex.createdAt ? new Date(ex.createdAt).toLocaleString() : '—'} · {ex.createdBy}</span>
                <Button variant="ghost" onClick={() => remove(ex.id)} data-testid={`tc-example-remove-${ex.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="hussar-tc-example-snip"><strong>Inquiry:</strong> {ex.rawInquiry}</div>
              {ex.reply && <div className="hussar-tc-example-snip"><strong>Reply:</strong> {ex.reply}</div>}
              {ex.notes && <div className="hussar-tc-example-snip hussar-tc-meta">{ex.notes}</div>}
            </li>
          ))}
          {items.length === 0 && <li className="hussar-tc-empty">No {type} examples yet.</li>}
        </ul>
      )}
    </div>
  );
};

/* ---------- 7. Feedback on real lead audits ---------- */
/* ---------- 7. Feedback ---------- */

// Compact "Brain v7 · Doctrine v2 · Reply v3 · Safety v1 · HVAC playbook · OpenAI"
// All segments hidden gracefully when their data is missing — never crashes
// on a row written before Phase 2 wired Company Brain into metadata.
const compactMetaSummary = (md) => {
  if (!md) return null;
  const parts = [];
  if (md.companyBrainPresent && md.companyBrainVersion) {
    parts.push(`Brain v${md.companyBrainVersion}`);
  } else if (md.companyBrainPresent === false) {
    parts.push('Brain —');
  }
  if (md.doctrineVersion) {
    // doctrineVersion is an ISO timestamp; show short date
    const d = new Date(md.doctrineVersion);
    if (!Number.isNaN(d.getTime())) {
      parts.push(`Doctrine ${d.toLocaleDateString()}`);
    }
  }
  const prompts = md.agentPrompts || [];
  const reply = prompts.find(p => p.agent === 'reply');
  if (reply) parts.push(`Reply v${reply.version}`);
  const safety = prompts.find(p => p.agent === 'safety');
  if (safety && md.safetyApplied) parts.push(`Safety v${safety.version}`);
  if (md.playbook) parts.push(`${md.playbook.toUpperCase()} playbook`);
  if (md.model) {
    const m = String(md.model).toLowerCase();
    const provider = m.startsWith('openai') ? 'OpenAI'
      : m.startsWith('anthropic') ? 'Anthropic'
      : m.startsWith('gemini') ? 'Gemini'
      : md.model;
    parts.push(provider);
  }
  return parts.length ? parts.join(' · ') : null;
};

const GenMetaPanel = ({ md, submissionId }) => {
  const [open, setOpen] = useState(false);
  if (!md) {
    return (
      <div className="hussar-tc-gen-meta hussar-tc-gen-meta-empty"
           data-testid={`tc-feedback-genmeta-${submissionId}`}>
        <span className="hussar-tc-meta">No generation metadata recorded</span>
      </div>
    );
  }
  const summary = compactMetaSummary(md);
  return (
    <div className="hussar-tc-gen-meta" data-testid={`tc-feedback-genmeta-${submissionId}`}>
      <button
        type="button"
        className="hussar-tc-gen-meta-summary"
        onClick={() => setOpen((v) => !v)}
        data-testid={`tc-feedback-genmeta-toggle-${submissionId}`}
        aria-expanded={open}
      >
        <span className="hussar-tc-gen-meta-icon">{open ? '▾' : '▸'}</span>
        <span data-testid={`tc-feedback-genmeta-compact-${submissionId}`}>
          {summary || 'metadata available'}
        </span>
        {md.safetyApplied && (md.safetyFindings || []).length > 0 && (
          <span className="hussar-tc-tag-reject">
            safety: {(md.safetyFindings || []).join(', ')}
          </span>
        )}
        {md.fallback && <span className="hussar-tc-tag-reject">fallback</span>}
      </button>
      {open && (
        <dl className="hussar-tc-gen-meta-details"
            data-testid={`tc-feedback-genmeta-details-${submissionId}`}>
          <dt>Company Brain</dt>
          <dd>
            id <code>{md.companyBrainId || '—'}</code> · version v{md.companyBrainVersion ?? 0}
            {md.companyBrainUpdatedAt && (
              <> · updated {new Date(md.companyBrainUpdatedAt).toLocaleString()}</>
            )}
          </dd>
          {(md.companyBrainFieldsUsed || []).length > 0 && (
            <>
              <dt>Brain fields used</dt>
              <dd>
                {(md.companyBrainFieldsUsed || []).map((f) => (
                  <code key={f} className="hussar-tc-mini-code">{f}</code>
                ))}
              </dd>
            </>
          )}
          <dt>Doctrine version</dt>
          <dd>{md.doctrineVersion || '—'}</dd>
          <dt>Agent prompts</dt>
          <dd>
            {(md.agentPrompts || []).length === 0 ? '—' :
              (md.agentPrompts || []).map((p) => (
                <code key={p.agent} className="hussar-tc-mini-code">{p.agent} v{p.version}</code>
              ))}
          </dd>
          <dt>Tone rules count</dt>
          <dd>{md.toneRulesCount ?? 0}</dd>
          <dt>Industry playbook</dt>
          <dd>{md.playbook || '—'}</dd>
          <dt>Safety</dt>
          <dd>
            {md.safetyApplied ? 'applied' : 'not applied'}
            {(md.safetyFindings || []).length > 0 && (
              <> · findings: {(md.safetyFindings || []).join(', ')}</>
            )}
          </dd>
          <dt>Model / provider</dt>
          <dd>{md.model || '—'}</dd>
          <dt>Generated at</dt>
          <dd>{md.generatedAt ? new Date(md.generatedAt).toLocaleString() : '—'}</dd>
        </dl>
      )}
    </div>
  );
};

const FeedbackTab = ({ setTab }) => {
  const api = useApi();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get('/lead-audits?limit=30');
    setSubs(r.data.submissions || []);
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const give = async (submissionId, kind) => {
    setBusyKey(`${submissionId}:${kind}`);
    try { await api.post('/feedback', { submissionId, kind }); await load(); }
    finally { setBusyKey(null); }
  };

  if (loading) return <Loader2 className="hussar-tc-spinner animate-spin" />;
  return (
    <div data-testid="tc-feedback">
      <Card className="hussar-tc-card">
        <h2 className="hussar-tc-h2">Recent lead-audit outputs</h2>
        <p className="hussar-tc-help">
          Click a feedback button on any output. Feedback is stored with the original
          input and the generated output as a snapshot. Expand any row&rsquo;s metadata
          chip to see exactly which Brain / Doctrine / prompt versions produced it.
        </p>
      </Card>

      <ul className="hussar-tc-feedback-list" data-testid="tc-feedback-list">
        {subs.map(s => (
          <li key={s.submissionId} className="hussar-tc-feedback-row" data-testid={`tc-feedback-${s.submissionId}`}>
            <div className="hussar-tc-feedback-head">
              <span className="hussar-tc-tag">{s.flow || '—'}</span>
              <span className="hussar-tc-meta">{s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'} · {s.payload?.email || '—'} · {s.payload?.city || '—'}</span>
              <span className="hussar-tc-meta">feedback: {s.feedbackCount}</span>
            </div>

            <GenMetaPanel md={s.generationMetadata} submissionId={s.submissionId} />

            <div className="hussar-tc-example-snip"><strong>Inquiry:</strong> {s.payload?.leadText || '—'}</div>
            {s.outputs?.lead_summary && (
              <div className="hussar-tc-example-snip"><strong>Summary:</strong> {s.outputs.lead_summary}</div>
            )}
            {s.outputs?.ready_reply && (
              <div className="hussar-tc-example-snip"><strong>Reply:</strong> {s.outputs.ready_reply.slice(0, 320)}{s.outputs.ready_reply.length > 320 ? '…' : ''}</div>
            )}

            <div className="hussar-tc-feedback-actions" data-testid={`tc-feedback-actions-${s.submissionId}`}>
              {FEEDBACK_KINDS.map(([kind, label]) => (
                <Button
                  key={kind}
                  variant={kind === 'approve' || kind === 'excellent_output' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => give(s.submissionId, kind)}
                  disabled={busyKey === `${s.submissionId}:${kind}`}
                  data-testid={`tc-feedback-${s.submissionId}-${kind}`}
                >
                  {busyKey === `${s.submissionId}:${kind}` ? <Loader2 className="w-3 h-3 animate-spin" /> : label}
                </Button>
              ))}
            </div>

            <div className="hussar-tc-feedback-links"
                 data-testid={`tc-feedback-links-${s.submissionId}`}>
              <Link
                to="/admin/company-brain"
                className="hussar-tc-feedback-link"
                data-testid={`tc-feedback-edit-brain-${s.submissionId}`}
              >
                Edit Company Brain
              </Link>
              <button
                type="button"
                className="hussar-tc-feedback-link"
                onClick={() => setTab && setTab('prompts')}
                data-testid={`tc-feedback-edit-prompts-${s.submissionId}`}
              >
                Edit Prompts
              </button>
              <button
                type="button"
                className="hussar-tc-feedback-link"
                onClick={() => setTab && setTab('log')}
                data-testid={`tc-feedback-view-log-${s.submissionId}`}
              >
                View Prompt Log
              </button>
            </div>
          </li>
        ))}
        {subs.length === 0 && <li className="hussar-tc-empty">No lead-audit submissions with outputs yet.</li>}
      </ul>
    </div>
  );
};

/* ---------- 8. Prompt Improvement Log ---------- */
const PromptLogTab = () => {
  const api = useApi();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const q = filter ? `?agent=${filter}` : '';
    api.get(`/prompt-log${q}`).then(r => setEntries(r.data.entries || [])).finally(() => setLoading(false));
  }, [api, filter]);

  if (loading) return <Loader2 className="hussar-tc-spinner animate-spin" />;
  return (
    <div data-testid="tc-log">
      <div className="hussar-tc-agent-row">
        <button type="button" onClick={() => setFilter('')} className={`hussar-tc-agent-pill ${filter === '' ? 'is-active' : ''}`} data-testid="tc-log-filter-all">All</button>
        {AGENTS.map(a => (
          <button key={a} type="button" onClick={() => setFilter(a)} className={`hussar-tc-agent-pill ${filter === a ? 'is-active' : ''}`} data-testid={`tc-log-filter-${a}`}>{a.replace('_', ' ')}</button>
        ))}
      </div>
      <ul className="hussar-tc-log-list" data-testid="tc-log-list">
        {entries.map(e => (
          <li key={e.id} className="hussar-tc-log-row" data-testid={`tc-log-${e.id}`}>
            <div className="hussar-tc-feedback-head">
              <span className="hussar-tc-tag">{e.agent}</span>
              <span className="hussar-tc-meta">v{e.fromVersion} → v{e.toVersion} · {e.updatedAt ? new Date(e.updatedAt).toLocaleString() : '—'} · {e.updatedBy}</span>
            </div>
            {e.note && <div className="hussar-tc-example-snip"><strong>Reason:</strong> {e.note}</div>}
            <div className="hussar-tc-log-diff">
              <div><strong>Before</strong><pre>{e.before}</pre></div>
              <div><strong>After</strong><pre>{e.after}</pre></div>
            </div>
          </li>
        ))}
        {entries.length === 0 && <li className="hussar-tc-empty">No prompt changes yet.</li>}
      </ul>
    </div>
  );
};

export default TrainingCenter;
